import React, { useMemo, memo } from "react";
import type { BiddingInfoResult, BiddingInfoData } from "../../../types";
import { useTruncatedTooltip } from "../../common/TruncatedTooltip";
import { useAuth } from "../../../contexts/AuthContext";
import { UserRole } from "../../../types/UserRole";
import { UserPermission } from "../../../types/UserPermission";
import { Heart } from "lucide-react";
import { useToggleFavoriteMutation } from "../../../hooks/useBiddingItemsQuery";
import { 
  formatJSTWithMillis, 
  calculateTotalWeight, 
  getCaltivationLabel, 
  getProductionLabel,
  getDisplayBidResultText
} from './biddingFormatHelpers';

//----------------
// Props
//----------------
interface BiddingResultItemRowProps {
  item: BiddingInfoResult;
  isExpanded: boolean;
  onToggle: () => void;
  isFavorite: boolean;
  isSellerLayout?: boolean;
}

//--------------------------------
// 落札結果の行コンポーネント
//--------------------------------
export const BiddingResultItemRow = memo(
  ({ item, isExpanded, onToggle, isFavorite, isSellerLayout }: BiddingResultItemRowProps) => {
    const { tooltipProps, TooltipElement } = useTruncatedTooltip(
      "font-medium truncate pointer-events-auto",
    );

    const { currentUser } = useAuth(); // 認証Context
    const loginId = currentUser?.loginId || "";
    const isBuyer = currentUser?.role === UserRole.Buyer;
    const userPerm = Number(currentUser?.permission);
    const canShowBidCount = currentUser?.role === UserRole.Admin && 
      (userPerm === UserPermission.Admin || userPerm === UserPermission.Creator);
    
    // 更新用Mutation
    const { mutate, isPending } = useToggleFavoriteMutation(loginId);

    //----------------
    // Memo
    //----------------

    // 入札件数の取得
    const bidCount = item.biddingInfoDatas?.length || 0;

    // // 最高落札価格の表示
    // const displayHighestPrice = useMemo(() => {
    //   if (!item.highestBidPrice || item.highestBidPrice === 0)
    //     return (
    //       <span className="text-slate-400">
    //         <br />
    //       </span>
    //     );
    //   return (
    //     <span className="text-red-600 font-bold">
    //       {Number(item.highestBidPrice).toLocaleString()}
    //     </span>
    //   );
    // }, [item.highestBidPrice]);
    // 最高落札価格の表示
    const displayHighestPrice = useMemo(() => {
      if (!item.highestBidPrice || item.highestBidPrice === 0) {
        return (
          <span className="text-slate-400">
            <br />
          </span>
        );
      }

      // 買手以外 かつ 「落札(抽)」の場合を判定
      const isNotBuyer = currentUser?.role !== UserRole.Buyer;
      const isLotteryWin = item.resultMsg === "落札(抽)";
      const showLottery = isNotBuyer && isLotteryWin;

      if (isNotBuyer){
        return (
          <span className="text-red-600 font-bold">
            {Number(item.highestBidPrice).toLocaleString()}
            {/* 条件を満たさない場合は invisible で透明にし、幅だけを確保する */}
              <span className={showLottery ? "" : "invisible select-none"}>
                (抽)
              </span>
          </span>
        );
      }else{
        return (
          <span className="text-red-600 font-bold">
            {Number(item.highestBidPrice).toLocaleString()}
          </span>
        );
      }
    }, [item.highestBidPrice, item.resultMsg, currentUser?.role]);

    // 落札結果の表示
    // --- 修正後：役割によって「何を見て判定するか」を分ける ---
    const displatBidResult = useMemo(() => {
      // 1. Helperから表示すべき文字列を取得
      const resultText = getDisplayBidResultText(
        item, 
        currentUser?.role, 
        Number(currentUser?.permission)
      );

      // 2. 空文字の場合は改行タグのみ返す
      if (!resultText) {
        return <span className="text-slate-400"><br /></span>;
      }

      // 3. 買手の場合のスタイル出し分け
      if (currentUser?.role === UserRole.Buyer) {
        if (resultText.includes("他社落札")) {
          return (
            <span className="text-[14px] text-slate-400 font-medium bg-slate-100 px-2 py-0.5 rounded-md">
              {resultText}
            </span>
          );
        }
        return (
          <span className="text-base lg:text-sm text-red-600 font-bold">
            {resultText}
          </span>
        );
      }

      // 4. 市場（Admin）等のスタイル
      return <span className="text-red-600 font-bold">{resultText}</span>;

    }, [item, currentUser?.role, currentUser?.permission]);

    // デバッグ用ハンドラ
    const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation(); // 念の為バブリング防止
      console.log("Row clicked:", item.itemId); // ★コンソールにIDが出るか確認
      onToggle();
    };

    // 落札者名の表示(売手明細用)
    const displayHighestBuyerName = useMemo(() => {
    // highestBidBuyerName 直接使う
    const name = item.highestBidBuyerName;

    // 名前が空（まだ落札されていない、またはデータがない）場合の処理
    if (!name) {
    return (
      <span className="text-slate-400">
        <br />
        </span>
      );
    }
  
    // 名前があれば太字で表示
    return <span className="font-bold">{name}</span>;
    }, [item.highestBidBuyerName]); 

    const handleToggleFavorite = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (isPending || !loginId) return;
      mutate({ itemId: item.itemId, branchNumber: item.branchNumber });
    };

    //生産者表示有無フラグ判定ロジック
    const canSeeFarmerName = useMemo(() => {
      // 1. 管理者は常に表示
      if (currentUser?.role === UserRole.Admin) return true;

      // 2. 自分の出品物なら常に表示
      // String() で囲むことで、数値と文字列の比較ミスを防ぎます
      const isOwnItem =
        item.sellerCode &&
        currentUser?.parentCode &&
        String(item.sellerCode) === String(currentUser.parentCode);
      if (isOwnItem) return true;

      // 3. それ以外（買手、または他人の出品物を見る売手）
      // 「隠す設定(Hidden)」が true なら、表示判定は false にする
      return !item.isFarmerHidden;
    }, [
      currentUser?.role,
      currentUser?.parentCode,
      item.sellerCode,
      item.isFarmerHidden, // バックエンドから届くようになったフラグ
    ]);

    //----------------
    // Render
    //----------------
    return (
      <div
        className={`mt-2 lg:mt-4 border-b lg:pb-0 border-slate-800 lg:border lg:rounded-lg lg:shadow-sm overflow-hidden bg-white ${isExpanded ? "ring-2 ring-indigo-500 rounded-lg" : ""}`}
      >
        {/* === ヘッダー行 (常に表示) === */}
        <div className="p-2 lg:p-4 cursor-pointer" onClick={handleClick}>
          <div
            className={`grid grid-cols-6 md:grid-cols-33 gap-2 items-center text-sm pointer-events-none`}
          >
            {isBuyer && (
              <div className="col-span-1 order-1 lg:order-none lg:col-span-1 min-w-[3rem] flex flex-col items-center">
                <button
                  onClick={handleToggleFavorite}
                  disabled={isPending}
                  className="pointer-events-auto p-1 -ml-8 rounded-full hover:bg-slate-100 transition-colors cursor-pointer disabled:opacity-50"
                  type="button"
                >
                  <Heart className={`... ${isFavorite ? "fill-red-400 text-red-400" : "text-slate-400"}`} />
                </button>
              </div>
            )}

            {/* 1. 入札番号 */}
            <div className="col-span-1 lg:col-span-2 order-1 lg:order-none">
              <p className="hidden md:block text-xs text-slate-500 mb-0.5">
                入札No.
              </p>
              <p className="text-base lg:font-bold text-slate-800">
                {item.biddingNumber || "-"}
              </p>
            </div>
            {/* 2. 売り手 */}
            <div className="col-span-4 lg:col-span-4 order-2 lg:order-none">
              <p className="hidden md:block text-xs text-slate-500 mb-0.5">
                売手
              </p>
              <p
                {...tooltipProps}
                className="text-lg lg:font-bold pointer-events-auto truncate"
              >
                {item.sellerName || "-"}
              </p>
            </div>
            {/* 3. 生産者 */}
            <div className="col-span-4 lg:col-span-4 order-3 lg:order-none">
              <p className="hidden lg:block text-xs text-slate-500 mb-0.5">
                生産者
              </p>
              <p
                {...tooltipProps}
                className="text-lg lg:font-bold pointer-events-auto truncate"
              >
                {/* 権限がある場合は名前、ない場合は「-」を表示 */}
                {canSeeFarmerName ? (
                  item.farmerName
                ) : (
                  <span className="text-slate-400 font-normal">-</span>
                )}
              </p>
            </div>
            {/* 4. 荷印 */}
            <div className="col-span-2 lg:col-span-3 order-4 lg:order-none">
              <p className="hidden lg:block text-xs text-slate-500 mb-0.5">
                荷印
              </p>
              <p
                {...tooltipProps}
                className="text-lg lg:font-bold pointer-events-auto truncate"
              >
                {item.brand || "-"}
              </p>
            </div>
            {/* PCでは非表示・茶期・茶種・品種 */}
            {/* 5. 茶期 */}
            <div className="col-span-2 hidden md:block">
              <p className="text-xs text-slate-500 mb-0.5">茶期</p>
              <p {...tooltipProps} className="text-lg font-bold truncate">
                {item.teaSeasonName || "-"}
              </p>
            </div>
            {/* 6. 茶種 */}
            <div className="col-span-2 hidden md:block">
              <p className="text-xs text-slate-500 mb-0.5">茶種</p>
              <p {...tooltipProps} className="text-lg font-bold truncate">
                {item.teaTypeName || "-"}
              </p>
            </div>
            {/* 7. 品種 */}
            <div className="col-span-3 hidden md:block">
              <p className="text-xs text-slate-500 mb-0.5">品種名</p>
              <p {...tooltipProps} className="text-lg font-bold pointer-events-auto truncate">
                {item.varietyName || "-"}
              </p>
            </div>
            {/* 8. 総量 */}
            <div className="hidden lg:block col-span-2 text-right">
              <p className="hidden md:block text-xs text-slate-500 mb-0.5">
                総量(kg)
              </p>
              <p className="text-lg lg:font-bold">
                {calculateTotalWeight(item.capacity1, item.quantity1, item.capacity2, item.quantity2).toLocaleString()}
              </p>
            </div>
            {/* 9. 入札件数 */}
            {canShowBidCount && (
            <div className="hidden lg:block col-span-2 text-center border-l border-slate-100 pl-2">
              <p className="text-xs text-slate-500 mb-0.5">入札件数</p>
              <p className="text-lg font-bold text-slate-700">
                {bidCount > 0 ? (
                  <span className="bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded-full text-sm">{bidCount}件</span>
                ) : (
                  <span className="text-slate-400 text-sm">0件</span>
                )}
              </p>
            </div>
            )}

            {/* 10. 自身の入札価格 */}
            {currentUser?.role === UserRole.Buyer && (
              <div className="hidden lg:block col-span-3 text-right border-l border-slate-100 pl-2">
                <p className="text-xs text-slate-500 mb-0.5">入札額(円)</p>
                <p className="text-lg font-bold text-blue-700">
                  {Number(item.myBidPrice).toLocaleString()}
                </p>
              </div>
            )}
            
            {/* X. 売手用落札者名表示 */}
            {/* {isSellerLayout && (
              <div className="hidden lg:block col-span-2 text-left border-l border-slate-100 pl-2 min-w-0">
                <p className="text-xs text-slate-500 mb-0.5">落札者名</p>
                <p {...tooltipProps} className="text-lg pointer-events-auto truncate">
                  {displayHighestBuyerName}
                </p>
              </div>
            )} */}
            {!isBuyer && (
              <div className="hidden lg:block col-span-5 text-left border-l border-slate-100 pl-2 min-w-0">
                <p className="text-xs text-slate-500 mb-0.5">落札者名</p>
                <p {...tooltipProps} className="text-lg text-blue-400 pointer-events-auto truncate">
                  {displayHighestBuyerName}
                </p>
              </div>
            )}
            
            {/* 11. 最高落札価格 */}
            <div className="hidden lg:block col-span-3 text-right border-l border-slate-100 pl-2">
              <p className="text-xs text-slate-500 mb-0.5">
                最高落札値(円)
              </p>
              <p className="text-lg">{displayHighestPrice}</p>
            </div>
            {/* 12. 落札結果 */}
            {currentUser?.role === UserRole.Buyer && (
              <div className="hidden lg:block col-span-3 text-center border-l border-slate-100 pl-2">
                <p className="text-xs text-slate-500 mb-0.5">落札結果</p>
                <p className="text-lg">{displatBidResult}</p>
              </div>
            )}
          </div>
        </div>

        <div className="lg:hidden w-full mt-1">
          {/* flex + divide-x で、要素の数に合わせて自動等分 & 境界線 */}
          <div className="flex items-center w-full bg-slate-100 rounded-lg py-1 -translate-y-3 divide-x divide-slate-300">
            {/* 1. 自身の入札価格 or 落札者名 */}
            {currentUser?.role === UserRole.Buyer ? (
              <div className="flex-1 px-1 text-center min-w-0">
                <span className="text-[10px] font-bold text-slate-500 leading-none mb-0.5">自身の入札</span>
                <p className="text-lg font-bold text-blue-700 truncate">
                  {Number(item.myBidPrice).toLocaleString()}
                </p>
              </div>
            // ) : isSellerLayout ? (
            //   // スマホ側の落札者名にもツールチップと省略表示を適用
            //   <div className="flex-1 px-1 text-center min-w-0">
            //     <span className="text-[10px] font-bold text-slate-500 leading-none mb-0.5">落札者</span>
            //     <p {...tooltipProps} className="text-lg pointer-events-auto truncate">
            //       {displayHighestBuyerName}
            //     </p>
            //   </div>
            // ) : null}
            ) : 
              // スマホ側の落札者名にもツールチップと省略表示を適用
              <div className="flex-1 px-1 text-center min-w-0">
                <span className="text-[10px] font-bold text-slate-500 leading-none mb-0.5">落札者</span>
                <p {...tooltipProps} className="text-lg pointer-events-auto truncate">
                  {displayHighestBuyerName}
                </p>
              </div>
            }

            {/* 2. 最高落札価格 (共通) */}
            <div className="flex-1 px-1 text-center">
              <span className="text-[10px] font-bold text-slate-500 leading-none mb-0.5">最高落札値</span>
              <p className="text-lg font-semibold">{displayHighestPrice}</p>
            </div>

            {/* 3. 落札結果 (Buyerのみ表示) */}
            {currentUser?.role === UserRole.Buyer && (
              <div className="flex-1 px-1 text-center min-w-0">
                <span className="text-[10px] font-bold text-slate-500 leading-none mb-0.5">結果</span>
                <p className="text-lg font-bold truncate">{displatBidResult}</p>
              </div>
            )}
          </div>
        </div>

        {/* モバイル用: 隠れている項目の簡易表示 */}
        <div className="md:hidden text-base flex flex-wrap gap-x-3 pointer-events-none justify-left ml-2.5 -translate-y-1.5">
          <span>{item.teaSeasonName}</span>
          <span>/</span>
          <span>{item.teaTypeName}</span>
          <span>/</span>
          <span>{item.varietyName}</span>
          <span>/</span>
          <span>{calculateTotalWeight(item.capacity1, item.quantity1, item.capacity2, item.quantity2).toLocaleString()}kg</span>
          {canShowBidCount && (
            <>
              <span>/</span>
              <span>{bidCount}件</span>
            </>
          )}
        </div>

        {/* === アコーディオン詳細 === */}
        {isExpanded && (
        <div className="p-1 lg:p-4 bg-slate-50 border-t border-slate-200 animate-fadeIn">
        <div className="grid grid-cols-3 lg:grid-cols-6 gap-y-4 gap-x-2 lg:gap-6">
          {/* 1. GAP区分 */}
          <div>
            <p className="hidden lg:block text-xs font-bold text-slate-500 uppercase mb-1">GAP区分</p>
            <p className="text-base text-slate-700 lg:font-semibold truncate">{item.gapName}</p>
          </div>
            
          {/* 2. 栽培区分 */}
          <div>
            <p className="hidden lg:block text-xs font-bold text-slate-500 uppercase mb-1">栽培区分</p>
            <p className="text-base text-slate-700 lg:font-semibold">
              {getCaltivationLabel(item.caltivationCategory) || "-"}
            </p>
          </div>
          
          {/* 3. 生産履歴 */}
          <div>
            <p className="hidden lg:block text-xs font-bold text-slate-500 uppercase mb-1">生産履歴</p>
            <p className="text-base text-slate-700 lg:font-semibold">
              {getProductionLabel(item.productionHistory) || "-"}
            </p>
          </div>
          
          {/* 4. 容量① */}
          <div>
            <p className="hidden lg:block text-xs font-bold text-slate-500 mb-1">容量(kg)/本数(本) ①</p>
            <p className="text-base text-slate-700 lg:font-semibold truncate">
              {item.capacity1}×{item.quantity1}
            </p>
          </div>
          
          {/* 5. 容量② */}
          <div>
            <p className="hidden lg:block text-xs font-bold text-slate-500 mb-1">容量(kg)/本数(本) ②</p>
            <p className="text-base text-slate-700 lg:font-semibold truncate">
              {item.capacity2 ? `${item.capacity2}×${item.quantity2}` : "-"}
            </p>
          </div>
          
          {/* 6. 最低落札値 */}
          <div>
            <p className="hidden lg:block text-xs font-bold text-slate-500 uppercase mb-1 text-right">最低落札値(円)</p>
            <p className="text-base text-slate-700 lg:font-semibold text-right truncate">
              {Number(item.bidMinPrice).toLocaleString()}
            </p>
          </div>
        </div>
          
        {/* 2段目：備考 (Buyer以外の場合のみ表示) */}
        {/* {currentUser?.role !== UserRole.Buyer && ( */}
          <div className="mt-4 pt-4 border-t border-slate-200">
            <p className="hidden lg:block text-xs font-bold text-slate-500 uppercase">備考</p>
            <p {...tooltipProps} className="text-base text-slate-700 lg:font-semibold leading-relaxed">
              {item.biko || "-"}
            </p>
          </div>
        {/* )} */}

            {/* 入札履歴テーブル (Adminかつ売手レイアウト「以外」の場合のみ表示) */}
            {/* 市場の編集者/閲覧者(isSellerLayoutがtrue)の場合は入札履歴を表示させない */}
            {currentUser?.role === UserRole.Admin && !isSellerLayout && (
              <div className="border-t border-slate-200 mt-1 ">
                <h4 className="text-sm font-bold text-slate-700 mb-3 mt-3 flex items-center gap-2">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    ></path>
                  </svg>
                  入札履歴
                </h4>
                {!item.biddingInfoDatas ||
                item.biddingInfoDatas.length === 0 ? (
                  <p className="text-sm text-slate-400 italic p-2">
                    入札履歴はありません。
                  </p>
                ) : (
                  <div className="overflow-x-auto border rounded-md bg-white">
                    <table className="w-full text-sm text-left text-slate-600">
                      <thead className="text-xs text-slate-500 uppercase bg-slate-100 border-b">
                        <tr>
                          <th className="px-4 py-2">入札日時</th>
                          <th className="px-4 py-2">買手名称</th>
                          <th className="px-4 py-2 text-right">入札本数①</th>
                          <th className="px-4 py-2 text-right">入札本数②</th>
                          <th className="px-4 py-2 text-right">入札総量</th>
                          <th className="px-4 py-2 text-right">入札値</th>
                          <th className="px-4 py-2 text-center">結果</th>
                        </tr>
                      </thead>
                      <tbody>
                        {item.biddingInfoDatas.map((bid, index) => (
                          <HistoryRow
                            key={`${bid.buyerCode}-${index}`}
                            bid={bid}
                            parentItem={item}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {TooltipElement}
      </div>
    );
  },
);

const HistoryRow = ({
  bid,
  parentItem,
}: {
  bid: BiddingInfoData;
  parentItem: BiddingInfoResult;
}) => {
  const bidTotalWeight = useMemo(() => {
    return calculateTotalWeight(
      parentItem.capacity1, bid.quantity1,
      parentItem.capacity2, bid.quantity2
    );
  }, [bid, parentItem]);

  //バックエンドから最高落札者判定を取得するように変更
  const isWinner = bid.isWinningBid;

  const formattedDate = formatJSTWithMillis(bid.bidTimestamp);

  return (
    <tr
      className={`border-b last:border-0 hover:bg-slate-50 ${isWinner ? "bg-yellow-50" : ""}`}
    >
      <td className="px-4 py-2 whitespace-nowrap text-xs text-slate-500">
        {formattedDate}
      </td>
      <td className="px-4 py-2 font-medium">{bid.buyerName}</td>
      <td className="px-4 py-2 text-right">{bid.quantity1}</td>
      <td className="px-4 py-2 text-right">{bid.quantity2}</td>
      <td className="px-4 py-2 text-right font-medium">{bidTotalWeight} kg</td>
      <td className="px-4 py-2 text-right font-bold text-slate-800 font-mono">
        {bid.bidPrice.toLocaleString()} 円
      </td>
      <td className="px-4 py-2 text-center">
        {isWinner && (
          <span className="inline-block px-2 py-0.5 rounded text-xs font-bold bg-red-100 text-red-600 border border-red-200">
            {parentItem.resultMsg}
          </span>
        )}
        {bid.isDisabled && (
          <span className="inline-block px-2 py-0.5 rounded text-xs font-bold bg-gray-100 text-gray-600 border border-gray-200">
            無効
          </span>
        )}
      </td>
    </tr>
  );
};